import type { ProgrammaticLandingPageConfig } from '../types'
import { CITY_YIELD_FACTS, NATIONAL_AVERAGE_YIELD_KWH_PER_KWP, type CityYieldFact } from './cityYieldFacts'

/**
 * SEO-4.0-Story Task 08 (docs/tasks/active/seo/seo-4.0/08-staedte-rollout-pilot.md): 250 city-yield
 * landing pages, one per `CITY_YIELD_FACTS` entry, generated from a single template instead of
 * hand-written per file (unlike the 21 kWp/18 kWh pages) - at this scale the differentiator
 * between pages is the real, distinct PVGIS number + ranking context per city, not individually
 * hand-crafted prose. DE-only (deliberate deviation from this story's "every new page in 3
 * languages" leitplanke, agreed with the user: German city/PLZ names have no realistic ES/PL
 * search intent, unlike the topical kWp/kWh/Wärmepumpe pages).
 *
 * Every number below (`annualYieldKwhPerKwp`, `rank`, `nationalAverageKwhPerKwp`, `monthlyShare`)
 * comes straight from `cityYieldFacts.ts` (itself generated by
 * `scripts/generate-city-yield-facts.mjs` from the public PVGIS API) - nothing here is invented or
 * hand-tuned per city.
 */

const HUB_PAGE_HREF = '/photovoltaik-ertrag-staedte-ranking'
const METHODIK_PAGE_HREF = '/methodik-preisberechnung'
const PV_RECHNER_HREF = '/pv-rechner'

function formatKwhDe(value: number): string {
  return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(value)} kWh`
}

function formatSignedPercentDe(value: number): string {
  const rounded = Math.round(value * 10) / 10
  const sign = rounded > 0 ? '+' : ''
  return `${sign}${rounded.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
}

/** Comparison phrase for the intro paragraph ("X % mehr/weniger als der Durchschnitt"). */
function comparisonPhrase(deltaPercent: number): string {
  if (Math.abs(deltaPercent) < 0.5) return 'in etwa auf Höhe des Durchschnitts aller 250 Städte'
  const direction = deltaPercent > 0 ? 'höher' : 'niedriger'
  return `${formatSignedPercentDe(deltaPercent).replace('+', '')} ${direction} als der Durchschnitt aller 250 Städte`
}

function buildCityPage(city: CityYieldFact, neighbors: { better?: CityYieldFact; worse?: CityYieldFact }): ProgrammaticLandingPageConfig {
  const deltaPercent =
    ((city.annualYieldKwhPerKwp - NATIONAL_AVERAGE_YIELD_KWH_PER_KWP) / NATIONAL_AVERAGE_YIELD_KWH_PER_KWP) * 100
  const roundedYield = Math.round(city.annualYieldKwhPerKwp)

  const relatedLinks: ProgrammaticLandingPageConfig['relatedLinks'] = []
  if (neighbors.better) {
    relatedLinks.push({
      label: `Photovoltaik-Ertrag in ${neighbors.better.name} (Platz ${neighbors.better.rank})`,
      href: `/photovoltaik-ertrag-${neighbors.better.slug}`,
    })
  }
  if (neighbors.worse) {
    relatedLinks.push({
      label: `Photovoltaik-Ertrag in ${neighbors.worse.name} (Platz ${neighbors.worse.rank})`,
      href: `/photovoltaik-ertrag-${neighbors.worse.slug}`,
    })
  }
  relatedLinks.push(
    { label: 'Ertrags-Ranking: Alle 250 Städte im Vergleich', href: HUB_PAGE_HREF },
    { label: 'Wie wir Ertrag und Ausrichtung berechnen (Methodik)', href: METHODIK_PAGE_HREF },
  )

  return {
    slug: `photovoltaik-ertrag-${city.slug}`,
    lang: 'de',
    topicId: `photovoltaik-ertrag-${city.slug}`,
    title: `Photovoltaik-Ertrag ${city.name}: ${roundedYield.toLocaleString('de-DE')} kWh pro kWp (PLZ ${city.plz})`,
    metaDescription:
      `Wie viel Ertrag liefert eine Photovoltaikanlage in ${city.name} (PLZ ${city.plz})? ` +
      `${formatKwhDe(city.annualYieldKwhPerKwp)} pro kWp und Jahr (Richtwert), Platz ${city.rank} von 250 ` +
      'deutschen Städten im Roofora-Ertrags-Ranking.',
    keywords: [
      `Photovoltaik Ertrag ${city.name}`,
      `Solarertrag ${city.name}`,
      `PV-Anlage ${city.name} kWh`,
      `Photovoltaik ${city.plz}`,
      `Solarertrag PLZ ${city.plz}`,
      `Lohnt sich Photovoltaik in ${city.name}`,
    ],
    eyebrow: `Städte-Ranking · Platz ${city.rank} von 250`,
    h1: `Photovoltaik-Ertrag in ${city.name}`,
    intro:
      `${city.name} (${city.bundesland}, PLZ ${city.plz}, rund ${city.population.toLocaleString('de-DE')} ` +
      `Einwohner) liegt im Roofora-Ertrags-Ranking der 250 einwohnerstärksten deutschen Städte auf ` +
      `Platz ${city.rank} von 250: Unter einer festen Referenz-Annahme (Süd-Ausrichtung, 35° Dachneigung, ` +
      `PVGIS-Daten) liefert eine Photovoltaikanlage hier rund ${formatKwhDe(city.annualYieldKwhPerKwp)} ` +
      `Ertrag pro kWp installierter Leistung und Jahr - das liegt ${comparisonPhrase(deltaPercent)} ` +
      `(${formatKwhDe(NATIONAL_AVERAGE_YIELD_KWH_PER_KWP)}).`,
    indexPolicy: 'index',
    cityYield: {
      citySlug: city.slug,
      cityName: city.name,
      bundesland: city.bundesland,
      annualYieldKwhPerKwp: city.annualYieldKwhPerKwp,
      monthlyShare: city.monthlyShare,
      rank: city.rank,
      totalRankedCities: CITY_YIELD_FACTS.length,
      nationalAverageKwhPerKwp: NATIONAL_AVERAGE_YIELD_KWH_PER_KWP,
    },
    sections: [
      { type: 'heading', text: `Wie wird der Ertragswert für ${city.name} berechnet?` },
      {
        type: 'paragraph',
        text:
          `Der Wert stammt aus PVGIS (Photovoltaic Geographical Information System der EU-Kommission), ` +
          `berechnet für eine Süd-Ausrichtung mit 35° Dachneigung - dieselbe Referenz-Neigung, die Rooforas ` +
          `eigenes Sizing-Tool als Optimalwert nutzt. Es handelt sich um einen Richtwert für ein ideal ` +
          `ausgerichtetes Dach an diesem Standort, nicht um eine Zusage für ein konkretes Gebäude: reale ` +
          `Dächer weichen in Ausrichtung, Neigung und Verschattung oft davon ab - wie stark, zeigt die ` +
          `Ausrichtungs-Ertragstabelle in der Methodik.`,
      },
      { type: 'heading', text: `Wie schneidet ${city.name} im bundesweiten Vergleich ab?` },
      {
        type: 'paragraph',
        text:
          `Von den 250 einwohnerstärksten deutschen Städten belegt ${city.name} Platz ${city.rank} - der ` +
          `Ertrag liegt ${comparisonPhrase(deltaPercent)}. Grund sind reale regionale Unterschiede in der ` +
          `Sonneneinstrahlung innerhalb Deutschlands: der Süden (insbesondere die Bodensee-Region) erhält ` +
          `spürbar mehr Einstrahlung als der Norden und die Mittelgebirgslagen.`,
      },
      { type: 'heading', text: 'Was beeinflusst den tatsächlichen Ertrag auf Ihrem Dach?' },
      {
        type: 'paragraph',
        text:
          `Der Standort ist nur ein Faktor. Tatsächliche Dachausrichtung, -neigung und Teilverschattung ` +
          `verändern den Ertrag zusätzlich, teils deutlich - eine dachspezifische Berechnung liefert der ` +
          `kostenlose PV-Rechner direkt für Ihre Adresse und Dachdaten.`,
      },
    ],
    faq: [
      {
        q: `Warum liegt der PV-Ertrag in ${city.name} bei etwa ${formatKwhDe(city.annualYieldKwhPerKwp)} pro kWp?`,
        a:
          `Dieser Wert basiert auf PVGIS-Einstrahlungsdaten für den Standort ${city.name} unter der ` +
          `Referenz-Annahme Süd-Ausrichtung/35° Neigung. Er unterscheidet sich zwischen deutschen Städten ` +
          `real und spürbar, weil die Sonneneinstrahlung regional unterschiedlich ist.`,
      },
      {
        q: `Ist ${formatKwhDe(city.annualYieldKwhPerKwp)} pro kWp ein guter Wert im deutschlandweiten Vergleich?`,
        a:
          `${city.name} belegt Platz ${city.rank} von 250 im Roofora-Ertrags-Ranking, ` +
          `${comparisonPhrase(deltaPercent)} (bundesweiter Durchschnitt der 250 Städte: ` +
          `${formatKwhDe(NATIONAL_AVERAGE_YIELD_KWH_PER_KWP)}).`,
      },
      {
        q: `Wie genau ist diese Schätzung für mein konkretes Dach in ${city.name}?`,
        a:
          `Es ist ein Richtwert für ein optimal ausgerichtetes Dach am Standort ${city.name}, keine ` +
          `Zusage für ein bestimmtes Gebäude. Tatsächliche Ausrichtung, Neigung und Verschattung Ihres ` +
          `Dachs verändern den realen Ertrag - eine genaue, dachspezifische Berechnung liefert der ` +
          `kostenlose PV-Rechner.`,
      },
    ],
    primaryCta: {
      label: `Anlagenertrag für Ihr Dach in ${city.name} berechnen`,
      action: 'link',
      href: PV_RECHNER_HREF,
    },
    secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
    relatedLinks,
  }
}

/** Rank-based neighbors (the city one place better/worse in the yield ranking) - the cross-linking
 *  equivalent of `getNeighborProgrammaticPages`'s size-neighbors for the kWp/kWh series, but city
 *  pages have no numeric size axis to sort by (see types.ts `CalculatorAxis` doc), so this uses
 *  `rank` instead. Computed once here at generation time rather than live at render time, since
 *  city pages are static content objects like the rest of this registry. */
function withRankNeighbors(sortedByRank: CityYieldFact[]): ProgrammaticLandingPageConfig[] {
  return sortedByRank.map((city, index) => {
    const better = index > 0 ? sortedByRank[index - 1] : undefined
    const worse = index < sortedByRank.length - 1 ? sortedByRank[index + 1] : undefined
    return buildCityPage(city, { better, worse })
  })
}

/** CITY_YIELD_FACTS is already sorted by rank ascending (see generate-city-yield-facts.mjs). */
export const STAEDTE_ERTRAG_PAGES: ProgrammaticLandingPageConfig[] = withRankNeighbors(CITY_YIELD_FACTS)

export const STAEDTE_ERTRAG_HUB_HREF = HUB_PAGE_HREF
